import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/ui/alert'
import { Button } from '@repo/ui/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { useFormSubmission } from '@/hooks/use-form-submission'
import { postJson } from '@/lib/utils/api'

const initialFormState = {
  name: '',
  email: '',
  subject: '',
  message: '',
}

const contactFallbackMessage =
  'We could not send this from the site right now. Email hello@zippity.app and we will reply with a useful next step.'

interface ContactFormProps {
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function ContactForm({ onSuccess, onError }: ContactFormProps) {
  const [formData, setFormData] = useState(initialFormState)
  const { status, errorMessage, submit } = useFormSubmission({
    submit: async (payload: typeof initialFormState) => {
      await postJson('/api/contact.send', payload)
    },
    onSuccess: () => {
      setFormData(initialFormState)
      onSuccess?.()
    },
    onError,
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await submit(formData)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }))
  }

  return (
    <Card className="rounded-sm border-border/75 bg-card/72 shadow-[0_24px_70px_-52px_color-mix(in_oklab,var(--foreground)_34%,transparent)] backdrop-blur-md">
      <CardHeader>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
          Send the details
        </p>
        <CardTitle className="text-2xl font-medium tracking-tight">
          Tell us what the week looks like.
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Jane Morgan"
                className="rounded-[4px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@company.com"
                className="rounded-[4px]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">What should we help with?</Label>
            <Input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="Calendar setup, service area, route fit..."
              className="rounded-[4px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Context</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              className="resize-none rounded-sm"
              placeholder="Tell us what kind of mobile work you do, where the schedule gets messy, and what you want the day to stop doing."
            />
          </div>

          {status === 'success' && (
            <Alert className="border-primary/40 bg-primary/5 text-foreground">
              <CheckCircle2 className="text-primary" />
              <AlertTitle>Message sent</AlertTitle>
              <AlertDescription>We will get back to you with a useful next step.</AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <Alert variant="destructive">
              <AlertTitle>Unable to send</AlertTitle>
              <AlertDescription>{formatContactError(errorMessage)}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={status === 'loading'}
            className="h-12 w-full rounded-[4px] active:translate-y-[1px]"
          >
            {status === 'loading' ? 'Sending...' : 'Send message'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function formatContactError(message: string | null) {
  if (!message || message === 'Request failed' || message === 'Service Unavailable') {
    return contactFallbackMessage
  }

  return message
}
